import React from 'react';
import {
    IsolatedSelectItem,
    NewModelId,
    OtherModelId,
    StrikeTeamsIsolatedSelectItem
} from "../../../data/DailyOperational";
import { CreateOtherModal } from "../Modal/CreateOtherModal";
import { AppContext } from "../../../AppContext";
import './IsolatedSelect.scss';
import { Editor } from 'react-draft-wysiwyg';
import { ContentState,  EditorState, SelectionState } from 'draft-js';
import commonService from '../../../aps2/services/CommonService';
import { CSSProperties } from 'styled-components';

type Props = {
    onChange: (id?: string, name?: string, styles?: string) => void,
    availableOptions: StrikeTeamsIsolatedSelectItem[],
    id?: string,
    name?: string,
    className?: string,
    showDefaultOptionName?: boolean,
    styles?: string
}
type State = {
    items: StrikeTeamsIsolatedSelectItem[],
    isOpenModal: boolean,
    uid?: string;
    allowColorPicker?: boolean,
    focus?: boolean;
    editorState?: EditorState | null,
    editorRef?: object;
    styles?: string;
    cssStyles?: CSSProperties;
}

export class StrikeTeamsReferenceSelect extends React.Component<Props, State> {
    static contextType = AppContext;
    state: State = {
        items: [],
        isOpenModal: false
    }

    private static otherOption: StrikeTeamsIsolatedSelectItem = {
        id: String(OtherModelId),
        name: 'Other'
    };

    private static newOption: StrikeTeamsIsolatedSelectItem = {
        id: String(NewModelId),
        name: ''
    };

    static getDerivedStateFromProps(props: Props, state: State) {
        if (props.availableOptions && props.availableOptions.length > 0) {
            const options = [...props.availableOptions];
            options.push(StrikeTeamsReferenceSelect.otherOption);
            options.push(StrikeTeamsReferenceSelect.newOption);
            options[options.length - 1].name = props.name ? props.name : '';

            return {
                items: options
            }
        }

        return null;
    }

    private getTemporarySelectedValue(): string | null {
        return this.state.items ? this.state.items[this.state.items.length - 1]?.name : null;
    }

    triggerModal = (): void => {
        this.setState({ isOpenModal: !this.state.isOpenModal });
    }

    handleModalSubmit = (name: string): void => {
        const items = [...this.state.items!];
        const newModelItem = items.find(i => i.id === String(NewModelId))!;
        newModelItem.name = name;

        this.setState({
            items: items
        });

        this.triggerModal();
        this.props.onChange(undefined, name);
    }

    handleModalClose = (): void => {
        this.triggerModal();

        if (!this.props.id && !this.props.name) return;

        if (this.props.id || this.props.name) {
            this.props.onChange(this.props.id, this.props.name);
        } else {
            this.props.onChange();
        }

    }

    handleOptionChanged = (e: React.ChangeEvent<HTMLSelectElement>) => {
        if (e.target.value === StrikeTeamsReferenceSelect.otherOption.id) {
            this.triggerModal();
        } else {
            let items = [...this.state.items];
            items[items.length - 1].name = '';
            this.props.onChange(e.target.value);
        }
    }

    componentDidMount(): void {
        this.setState({
            uid: commonService.getUniqueId(),
            allowColorPicker: !!document.querySelector(".doc-designer"),
            styles: this.props.styles,
            cssStyles: this.getCssStyles(this.props.styles || "")
        });
    }

    selectAllContent = () => {
        if(this.state.editorState) {
            const contentState = this.state.editorState.getCurrentContent();  
            const firstBlock = contentState.getFirstBlock();
            const lastBlock = contentState.getLastBlock();
                        
            const selection = SelectionState.createEmpty(firstBlock.getKey()).merge({
                anchorKey: firstBlock.getKey(),
                anchorOffset: 0,
                focusKey: lastBlock.getKey(),
                focusOffset: lastBlock.getLength(),
            });        
            this.setState({
                editorState: EditorState.forceSelection(this.state.editorState, selection)
            });      
        }
    };

    getCssStyles = (styleString: string) => {
       const styles: CSSProperties = { };
       if(styleString){
        const arr = styleString.split("|");
        //console.log(arr);
        arr.forEach(s => {
            if(s.startsWith("color-")) {
                styles.color = s.replaceAll("color-", "");
            } 
            if(s.startsWith("bgcolor-")) {
                styles.backgroundColor = s.replaceAll("bgcolor-", "");
            } 
        });
       }
       return styles; 
    }


    render() {
        const value = this.props.name ? StrikeTeamsReferenceSelect.newOption.id : this.props.id;
        const className = this.props.className || '';
        return (
            <section
                id={this.state.uid || ""} 
                className={`${className} isolated-select-border custom-select-without-arrow with-color-picker ${this.state.focus ? 'active': ''}`}>
                <select disabled={!this.context.isEditableDailyOperational}
                    className='custom-select-without-arrow'  
                    value={value || ''}
                    onChange={this.handleOptionChanged}
                    style={this.state.cssStyles}
                    onFocus={e => {
                        try {
                            const content = ContentState.createFromText("colors");
                            const state = EditorState.createWithContent(content);
                            
                            this.setState({ 
                                focus: true,
                                editorState: state
                            });
                            setTimeout(() => {
                                const toolbarElement = document.getElementById(this.state.uid || "")?.querySelector(".rdw-colorpicker-wrapper");
                                if (toolbarElement) {
                                toolbarElement.addEventListener("click", e => {
                                    //console.log('toolbar clicked');
                                    this.state.editorState && this.selectAllContent();
                                });
                                }
                            }, 200)
                            //console.log("worked");
                        } catch (error) {
                            this.setState({ 
                                focus: false
                            });
                        }
                    }}
                    onBlur={e => {
                        this.setState({ focus: false });
                    }}>
        <option value="">{this.props.showDefaultOptionName ? 'Select One' : ''}</option>
        {this.state.items && this.state.items.map(o =>
            <option hidden={o.id === StrikeTeamsReferenceSelect.newOption.id} key={o.id} value={String(o.id)}>{o.name}</option>
        )}
        </select>
        {this.state.allowColorPicker && this.state.focus &&
        <div>
            <Editor
                editorState={this.state.editorState ?? EditorState.createEmpty()}
                toolbar={{ 
                    options: ['colorPicker'],
                }}
                onEditorStateChange={e => {
                    this.setState({ editorState: e });
                    const currentStyle = e.getCurrentInlineStyle();
                    let arr = (this.state.styles || "").split("|");
                    currentStyle.forEach((style) => {
                        ["color-", "bgcolor-"].forEach(p => {
                            if (style?.startsWith(p)) {
                                if(arr.find(s => s.startsWith(p))) {
                                    arr = [...arr.filter(s => !s.startsWith(p)), style];
                                } else {
                                    arr.push(style);
                                }
                            }
                        });
                        
                    });
                    const newStyles = arr.filter(a => !!a).join("|");
                    this.setState({
                        styles: newStyles,
                        cssStyles: this.getCssStyles(newStyles)
                    });

                    this.props.onChange(value === null ? undefined: value, this.getTemporarySelectedValue() || undefined, newStyles);
                }}
            />              
        </div>}
        <CreateOtherModal isOpen={this.state.isOpenModal} submit={this.handleModalSubmit} previousValue={this.getTemporarySelectedValue()} close={this.handleModalClose}/>
        </section>
    )
    }
}
